import * as Sentry from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { jwtDecode, JwtPayload } from 'jwt-decode'
import React from 'react'
import { AuthInformation, User } from '../models/api'
import { getCountyFromJurisdiction } from '../utils/counties'
import { currentTaxYearUsePaymentsQueryKey } from '../utils/queryKeys'
import useLocalStorage from './useLocalStorage'

interface JwtCustomPayload extends JwtPayload {
  userId: number
  fullName: string
  taxYear: number
  jurisdictionId: number
  isMasterUser?: boolean
  isTelecomUser?: boolean
  isMcatUser?: boolean
}

interface AuthProviderProps {
  children: React.ReactNode
}

interface AuthContextType {
  user: User
  authInformation: AuthInformation
  setUser?: React.Dispatch<React.SetStateAction<boolean>>
  storeToken?: (token: string) => void
  signOut?: () => void
}

const AuthContext = React.createContext<AuthContextType>({} as AuthContextType)

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useLocalStorage<User | null>('user', null)
  const [authInformation, setAuthInformation] = useLocalStorage<AuthInformation | null>('authInformation', null)
  const queryClient = useQueryClient()

  // Session-scoped queries are cached indefinitely, so drop them whenever the session changes
  const clearSessionQueries = (): void => {
    queryClient.removeQueries({ queryKey: currentTaxYearUsePaymentsQueryKey })
  }

  const storeToken = (token: string): void => {
    const payload = jwtDecode<JwtCustomPayload>(token)
    const newAuthInformation: AuthInformation = {
      token: token,
      userId: payload.userId,
      jurisdictionId: payload.jurisdictionId,
      isMasterUser: payload.isMasterUser ?? false,
      isTelecomUser: payload.isTelecomUser ?? false,
      isMcatUser: payload.isMcatUser ?? false,
    }
    const county = getCountyFromJurisdiction(payload.jurisdictionId)
    const userInformation = {
      county,
      businessName: 'Utah County',
      accountNumber: payload.userId.toString(),
      fullName: payload.fullName.toString(),
    }

    clearSessionQueries()
    setAuthInformation(newAuthInformation)
    setUser(userInformation)

    Sentry.setUser({
      id: payload.userId.toString(),
      fullname: userInformation.fullName,
      countyId: userInformation.county.id,
      countyName: county?.name,
      jurisdictionId: payload.jurisdictionId?.toString() ?? '0',
      isMasterUser: payload.isMasterUser,
      isMcatUser: payload.isMcatUser,
    })
  }

  const signOut = (): void => {
    clearSessionQueries()
    setUser(null)
    setAuthInformation(null)
    Sentry.setUser(null)
  }

  const authContext = {
    user: user as User,
    authInformation: authInformation as AuthInformation,
    storeToken,
    signOut,
  }

  return <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>
}

export default function useAuth(): AuthContextType {
  return React.useContext(AuthContext)
}
